import { colors } from 'src/constants';
import { PurchaseOrderFilterActionEnum } from './enum';
import {
  PurchaseOrderFormValues,
  PurchaseOrderStatusOption,
  RoleBasedTransition
} from '../types';

export const initialEmptyProduct = {
  product_id: null,
  product_name: '',
  quantity: 1,
  dealer_price: 0,
  sku: '',
  category: undefined
};

export const initialPurchaseOrderValues: PurchaseOrderFormValues = {
  supplier_id: null,
  branch_id: null,
  delivery_date: null, // must match "YYYY-MM-DD" format for API
  remarks: '',
  products: []
};

export const PurchaseOrderStatuses = {
  // Main PO statuses
  DRAFT: 'draft',
  PENDING: 'pending',
  FOR_APPROVAL: 'for approval',
  APPROVED: 'approved',
  SENT: 'sent',
  CANCELLED: 'cancelled',
  DECLINED: 'declined',
  COMPLETED: 'completed',
  CLOSED: 'closed',
  FOR_CHECKING: 'for_checking',
  FOR_DELIVER: 'for_delivery',
  FOR_ACCOUNTING: 'for_accounting',
  FOR_COLLECT: 'for_collect',
  COLLECTED: 'collected',

  // Fulfillment statuses
  UNFULFILLED: 'unfulfilled',
  PARTIALLY_FULFILLED: 'partially_fulfilled',
  IN_TRANSIT: 'in_transit',
  RECEIVED: 'received',
  RETURNED: 'returned',
  INVENTORY_IN: 'inventory_in'
};

export const purchaseOrderStatusOptions: PurchaseOrderStatusOption[] = [
  {
    label: 'Draft',
    value: PurchaseOrderStatuses.DRAFT,
    description: 'PO is being created but not yet submitted.',
    color: colors.grey[500]
  },
  {
    label: 'Pending',
    value: PurchaseOrderStatuses.PENDING,
    description: 'Awaiting manager or admin approval.',
    color: colors.orange[600]
  },
  {
    label: 'For Approval',
    value: PurchaseOrderStatuses.FOR_APPROVAL,
    description: 'PO is ready for review and approval.',
    color: colors.orange[800]
  },
  {
    label: 'Approved',
    value: PurchaseOrderStatuses.APPROVED,
    description: 'PO is approved and ready to be sent to the supplier.',
    color: colors.lightBlue[500]
  },
  {
    label: 'Sent',
    value: PurchaseOrderStatuses.SENT,
    description: 'PO has been sent to the supplier.',
    color: colors.blue[500]
  },
  {
    label: 'Cancelled',
    value: PurchaseOrderStatuses.CANCELLED,
    description: 'PO was voided/cancelled before fulfillment.',
    color: colors.red[500]
  },
  {
    label: 'Rejected',
    value: PurchaseOrderStatuses.DECLINED,
    description: 'PO was reviewed but not approved.',
    color: colors.red[400]
  },
  {
    label: 'Completed',
    value: PurchaseOrderStatuses.COMPLETED,
    description: 'PO is fulfilled and closed.',
    color: colors.green[600]
  },
  {
    label: 'Closed',
    value: PurchaseOrderStatuses.CLOSED,
    description: 'Archived for record-keeping; no further action.',
    color: colors.grey[700]
  },
  {
    label: 'Unfulfilled',
    value: PurchaseOrderStatuses.UNFULFILLED,
    description: 'No items received yet.',
    color: colors.orange[400]
  },
  {
    label: 'Partially Fulfilled',
    value: PurchaseOrderStatuses.PARTIALLY_FULFILLED,
    description: 'Some items received, others pending.',
    color: colors.lightBlue[400]
  },
  {
    label: 'In Transit',
    value: PurchaseOrderStatuses.IN_TRANSIT,
    description: 'Supplier has dispatched the items.',
    color: colors.blue[400]
  },
  {
    label: 'Received',
    value: PurchaseOrderStatuses.RECEIVED,
    description: 'All items received.',
    color: colors.green[500]
  },
  {
    label: 'Returned',
    value: PurchaseOrderStatuses.RETURNED,
    description: 'Items were returned to supplier.',
    color: colors.red[300]
  },
  {
    label: 'Inventory In',
    value: PurchaseOrderStatuses.INVENTORY_IN,
    description: 'Goods are logged into inventory.',
    color: colors.green[400]
  },
  {
    label: 'For Delivery',
    value: PurchaseOrderStatuses.FOR_DELIVER,
    description: 'Supplier has dispatched the items.',
    color: colors.purple[400]
  },
  {
    label: 'For Collect',
    value: PurchaseOrderStatuses.FOR_COLLECT,
    description: 'Supplier has dispatched the items.',
    color: colors.purple[400]
  },
  {
    label: 'Done Checking',
    value: PurchaseOrderStatuses.FOR_CHECKING,
    description: 'Products are done checking for accounting.',
    color: colors.cyan[700]
  },
  {
    label: 'Collected',
    value: PurchaseOrderStatuses.COLLECTED,
    description: 'Buyer has received the items from the supplier.',
    color: colors.lightGreen[400]
  }
];

// Statuses below are intentionally ordered to reflect the real-life progression of a Purchase Order workflow
export const PurchaseOrderStatusesV2 = {
  // PO Staff
  DRAFT: 'draft',
  PENDING: 'pending',

  // PO Supervisor
  FOR_APPROVAL: 'for_approval',
  APPROVED: 'approved', // can be seen by po staff
  DECLINED: 'declined', // can be seen by po staff

  // PO staff
  FOR_DELIVERY: 'for_delivery', // can be seen by warehouse

  // Warehouse staff
  FOR_PRODUCT_IN: 'for_product_in', // can be seen by warehouse
  DONE_PRODUCT_IN: 'done_product_in',

  // PO staff
  FOR_CHECKING: 'for_checking',
  FOR_ACCOUNTING: 'for_accounting', // can be seen by accounting

  // Accounting staff
  FOR_COLLECT: 'for_collect',
  COLLECTED: 'collected',
  COMPLETED: 'completed',
  CANCEL: 'cancel'
};

export const purchaseOrderStatusOptionsV2: PurchaseOrderStatusOption[] = [
  {
    label: 'Draft',
    value: PurchaseOrderStatusesV2.DRAFT,
    description: 'PO is being created but not yet submitted.',
    color: colors.grey[500]
  },
  {
    label: 'For Approval',
    value: PurchaseOrderStatusesV2.FOR_APPROVAL,
    description: 'Awaiting manager or admin approval.',
    color: colors.amber[600]
  },
  {
    label: 'Approved',
    value: PurchaseOrderStatusesV2.APPROVED,
    description: 'PO is approved and ready to be sent to the supplier.',
    color: colors.lightBlue[500]
  },
  {
    label: 'Declined',
    value: PurchaseOrderStatusesV2.DECLINED,
    description: 'PO was declined before fulfillment.',
    color: colors.red[500]
  },
  {
    label: 'For Delivery',
    value: PurchaseOrderStatusesV2.FOR_DELIVERY,
    description: 'PO has delivery date from the supplier.',
    color: colors.deepPurple[400]
  },
  {
    label: 'For Product In', // do in the view and transistion
    value: PurchaseOrderStatusesV2.FOR_PRODUCT_IN,
    description: 'Warehouse has received items.',
    color: colors.deepPurple[400]
  },
  {
    label: 'Done Product In',
    value: PurchaseOrderStatusesV2.DONE_PRODUCT_IN,
    description: 'PO products are confirmed.',
    color: colors.blue[500] // may change once viewed
  },
  {
    label: 'For PO Checking',
    value: PurchaseOrderStatusesV2.FOR_CHECKING,
    description: 'Awaiting confirmation from PO staff',
    color: colors.teal[500]
  },
  {
    label: 'For Accounting',
    value: PurchaseOrderStatusesV2.FOR_ACCOUNTING,
    description: 'For review of accounting.',
    color: colors.yellow[600]
  },
  {
    label: 'For Collect',
    value: PurchaseOrderStatusesV2.FOR_COLLECT,
    description: 'Check is ready for collection.',
    color: colors.orange[700]
  },
  {
    label: 'Collected',
    value: PurchaseOrderStatusesV2.COLLECTED,
    description: 'Check for po has been collected.',
    color: colors.green[400]
  },
  {
    label: 'Completed',
    value: PurchaseOrderStatusesV2.COMPLETED,
    description: 'PO is fulfilled and closed.',
    color: colors.green[600]
  },
  {
    label: 'CANCELLED',
    value: PurchaseOrderStatusesV2.CANCEL,
    description: 'Cancel Purchase Order.',
    color: colors.red[800]
  }
];

export const statusOptionsPerRole: { [key: string]: string[] } = {
  purchaser: [
    PurchaseOrderStatusesV2.DRAFT,
    PurchaseOrderStatusesV2.APPROVED,
    PurchaseOrderStatusesV2.DECLINED,
    PurchaseOrderStatusesV2.FOR_DELIVERY,
    PurchaseOrderStatusesV2.FOR_APPROVAL,
    PurchaseOrderStatusesV2.FOR_CHECKING,
    PurchaseOrderStatusesV2.FOR_ACCOUNTING
  ],
  'Purchasing Product Specialist': [
    PurchaseOrderStatusesV2.DRAFT,
    PurchaseOrderStatusesV2.APPROVED,
    PurchaseOrderStatusesV2.DECLINED,
    PurchaseOrderStatusesV2.FOR_DELIVERY,
    PurchaseOrderStatusesV2.FOR_APPROVAL,
    PurchaseOrderStatusesV2.FOR_CHECKING,
    PurchaseOrderStatusesV2.FOR_ACCOUNTING,
    PurchaseOrderStatusesV2.CANCEL
  ],
  'Purchasing Product Specialist II': [
    PurchaseOrderStatusesV2.DRAFT,
    PurchaseOrderStatusesV2.APPROVED,
    PurchaseOrderStatusesV2.DECLINED,
    PurchaseOrderStatusesV2.FOR_DELIVERY,
    PurchaseOrderStatusesV2.FOR_APPROVAL,
    PurchaseOrderStatusesV2.FOR_CHECKING,
    PurchaseOrderStatusesV2.FOR_ACCOUNTING,
    PurchaseOrderStatusesV2.CANCEL
  ],
  'Purchasing Supervisor': [
    PurchaseOrderStatusesV2.FOR_APPROVAL,
    PurchaseOrderStatusesV2.APPROVED,
    PurchaseOrderStatusesV2.DECLINED
  ],
  'Warehouse Staff': [
    PurchaseOrderStatusesV2.FOR_DELIVERY,
    PurchaseOrderStatusesV2.FOR_PRODUCT_IN,
    PurchaseOrderStatusesV2.FOR_ACCOUNTING,
    PurchaseOrderStatusesV2.DONE_PRODUCT_IN,
    PurchaseOrderStatusesV2.FOR_CHECKING
  ],
  'Warehouse Supervisor': [
    PurchaseOrderStatusesV2.FOR_DELIVERY,
    PurchaseOrderStatusesV2.FOR_PRODUCT_IN,
    PurchaseOrderStatusesV2.FOR_ACCOUNTING,
    PurchaseOrderStatusesV2.DONE_PRODUCT_IN,
    PurchaseOrderStatusesV2.FOR_CHECKING
  ],
  'Accounting Staff': [
    PurchaseOrderStatusesV2.FOR_ACCOUNTING,
    PurchaseOrderStatusesV2.FOR_COLLECT,
    PurchaseOrderStatusesV2.COLLECTED,
    PurchaseOrderStatusesV2.COMPLETED,
    PurchaseOrderStatusesV2.FOR_PRODUCT_IN,
    PurchaseOrderStatusesV2.FOR_DELIVERY,
    PurchaseOrderStatusesV2.FOR_CHECKING
  ],
  // give owner all statuses
  owner: [
    PurchaseOrderStatusesV2.DRAFT,
    PurchaseOrderStatusesV2.PENDING,
    PurchaseOrderStatusesV2.APPROVED,
    PurchaseOrderStatusesV2.DECLINED,
    PurchaseOrderStatusesV2.FOR_DELIVERY,
    PurchaseOrderStatusesV2.FOR_CHECKING,
    PurchaseOrderStatusesV2.FOR_ACCOUNTING,
    PurchaseOrderStatusesV2.FOR_PRODUCT_IN,
    PurchaseOrderStatusesV2.DONE_PRODUCT_IN,
    PurchaseOrderStatusesV2.FOR_COLLECT,
    PurchaseOrderStatusesV2.COLLECTED,
    PurchaseOrderStatusesV2.COMPLETED,
    PurchaseOrderStatusesV2.FOR_APPROVAL,
    PurchaseOrderStatusesV2.CANCEL
  ]
};

export const allowedTransitions: { [key: string]: string[] } = {
  draft: ['for_approval'],
  for_approval: ['approved', 'declined', 'draft'],
  declined: ['for_approval'],
  approved: ['for_delivery', 'declined'],
  for_delivery: ['for_product_in'],
  for_product_in: ['done_product_in'],
  done_product_in: ['for_checking'],
  for_checking: ['for_accounting'],
  for_accounting: ['for_collect'],
  for_collect: ['collected'],
  collected: ['completed'],
  completed: ['cancel'],
  cancel: [
    'draft',
    'for_approval',
    'approved',
    'for_delivery',
    'for_product_in',
    'done_product_in',
    'for_checking',
    'for_accounting',
    'for_collect',
    'collected'
  ]
};

export const roleBasedTransitions: RoleBasedTransition[] = [
  {
    from: 'draft',
    to: 'for_approval',
    roles: [
      'purchaser',
      'Purchasing Product Specialist',
      'Purchasing Product Specialist II',
      'owner'
    ]
  },
  {
    from: 'for_approval',
    to: 'draft',
    roles: [
      'purchaser',
      'Purchasing Product Specialist',
      'Purchasing Product Specialist II',
      'owner'
    ]
  },
  {
    from: 'for_approval',
    to: 'approved',
    roles: [
      'Purchasing Supervisor',
      'Purchasing Product Specialist II',
      'owner'
    ]
  },
  {
    from: 'for_approval',
    to: 'declined',
    roles: [
      'Purchasing Supervisor',
      'Purchasing Product Specialist II',
      'owner'
    ]
  },
  {
    from: 'approved',
    to: 'declined',
    roles: [
      'Purchasing Supervisor',
      'Purchasing Product Specialist II',
      'owner'
    ]
  },
  {
    from: 'approved',
    to: 'for_delivery',
    roles: [
      'purchaser',
      'Purchasing Product Specialist',
      'Purchasing Product Specialist II',
      'owner'
    ]
  },
  {
    from: 'approved',
    to: 'for_approval',
    roles: ['purchaser', 'Purchasing Product Specialist II', 'owner']
  },
  {
    from: 'approved',
    to: 'declined',
    roles: ['Purchasing Supervisor', 'owner']
  },
  {
    from: 'declined',
    to: 'for_approval',
    roles: [
      'purchaser',
      'Purchasing Product Specialist',
      'Purchasing Product Specialist II',
      'owner'
    ]
  },
  {
    from: 'for_delivery',
    to: 'for_product_in',
    roles: ['Warehouse Staff', 'Warehouse Supervisor', 'owner']
  },
  {
    from: 'for_product_in',
    to: 'done_product_in',
    roles: ['Warehouse Staff', 'Warehouse Supervisor', 'owner']
  },
  {
    from: 'done_product_in',
    to: 'for_checking',
    roles: ['Warehouse Staff', 'Warehouse Supervisor', 'owner']
  },
  {
    from: 'for_checking',
    to: 'for_accounting',
    roles: [
      'purchaser',
      'Purchasing Product Specialist',
      'Purchasing Product Specialist II',
      'owner'
    ]
  },
  {
    from: 'for_accounting',
    to: 'for_collect',
    roles: ['Accounting Staff', 'owner']
  },
  {
    from: 'for_collect',
    to: 'collected',
    roles: ['Accounting Staff', 'owner']
  },
  { from: 'collected', to: 'completed', roles: ['Accounting Staff', 'owner'] }
];

export const purchaseOrderRoles: PurchaseOrderStatusOption[] = [
  {
    label: 'Purchasing Product Specialist',
    value: 'Purchasing Product Specialist',
    description: 'Purchasing Product Specialist role.',
    color: colors.blue[600]
  },
  {
    label: 'Purchasing Product Specialist II',
    value: 'Purchasing Product Specialist II',
    description: 'Purchasing Product Specialist II role.',
    color: colors.blue[600]
  },
  {
    label: 'PO Staff',
    value: 'purchaser',
    description: 'Purchaser role.',
    color: colors.blue[600]
  },
  {
    label: 'PO Supervisor',
    value: 'Purchasing Supervisor',
    description: 'Supervisor role.',
    color: colors.blue[600]
  },
  {
    label: 'Warehouse Staff',
    value: 'Warehouse Staff',
    description: 'Warehouse role.',
    color: colors.blue[600]
  },
  {
    label: 'Warehouse Supervisor',
    value: 'Warehouse Supervisor',
    description: 'Warehouse supervisor role.',
    color: colors.blue[600]
  },
  {
    label: 'Accounting Staff',
    value: 'Accounting Staff',
    description: 'Accounting role.',
    color: colors.blue[600]
  },
  {
    label: 'Owner',
    value: 'Owner',
    description: 'Owner role.',
    color: colors.blue[600]
  }
];

export const roles = [
  'purchaser',
  'purchasing product specialist',
  'purchasing product specialist II',
  'purchasing supervisor',
  'warehouse staff',
  'warehouse supervisor',
  'accounting staff',
  'owner'
];

export const allowedRoles = [
  'Purchasing Supervisor',
  'Purchasing Product Specialist II',
  'owner'
];

// eslint-disable-next-line no-undef
export const statusButtonTextMap: Record<string, string> = {
  draft: 'Submit for Approval',
  for_approval: 'Approve PO',
  approved: 'Ready for Delivery',
  for_delivery: 'Confirm Delivery',
  for_product_in: 'Confirm Product In',
  done_product_in: 'Send for PO Checking',
  for_checking: 'Send to Accounting',
  for_accounting: 'Prepare for Collection',
  for_collect: 'Mark as Collected',
  collected: 'Mark as Completed',
  completed: 'Completed'
};

export const purchaseOrderTableHeaders = [
  '',
  'Order Number',
  'Branch',
  'Supplier',
  'Encoded At',
  'PO Date',
  'Amount',
  'Categories',
  'Created By',
  'For Tax Certificate',
  'Status'
];

export const searchFilter = [
  PurchaseOrderFilterActionEnum.PurchaseOrderNo,
  PurchaseOrderFilterActionEnum.Product
];

export const allowedStatusToViewCheque = [
  PurchaseOrderStatusesV2.FOR_ACCOUNTING,
  PurchaseOrderStatusesV2.FOR_COLLECT,
  PurchaseOrderStatusesV2.COLLECTED,
  PurchaseOrderStatusesV2.COMPLETED
];

export const allowedStatusToEditTerms = [
  '',
  PurchaseOrderStatusesV2.DRAFT,
  PurchaseOrderStatusesV2.PENDING,
  PurchaseOrderStatusesV2.FOR_APPROVAL,
  PurchaseOrderStatusesV2.APPROVED,
  PurchaseOrderStatusesV2.DECLINED,
  PurchaseOrderStatusesV2.FOR_DELIVERY,
  PurchaseOrderStatusesV2.FOR_PRODUCT_IN,
  PurchaseOrderStatusesV2.DONE_PRODUCT_IN,
  PurchaseOrderStatusesV2.FOR_CHECKING,
  PurchaseOrderStatusesV2.FOR_ACCOUNTING,
  PurchaseOrderStatusesV2.FOR_COLLECT
];

export const ForTaxCertificateOptions = [
  {
    label: 'For 2307',
    value: 'for 2307'
  },
  {
    label: 'N/A',
    value: 'N/A'
  }
]